import React, { useContext, useEffect, useState } from 'react';

import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { GridRowParams, GridValueFormatterParams } from '@mui/x-data-grid';

import CustomDataGrid from '../../components/data-grid';
import Screen from '../../components/screen';
import SplashWait from '../../components/splash-wait';
import { AppContext } from '../../contexts/app-context';
import GlobalStyles from '../../global-styles';
import {
  checkUserAuthenticated, formatDateForDataGrid, goToRoutePage, setWindowTitle
} from '../../utils/helper';
import { AddIcon, RefreshIcon } from '../../utils/icons';
import {
  Box, Breadcrumbs, Grid, IconButton, Link, Tooltip, Typography
} from '../../utils/material-ui-core';
import AdminUserCURD from './crud/adminuser-curd';

const ManageAdminUser = () => {
  const pageStyles = LocalPageStyles();
  const globalStyles = GlobalStyles();

  const { onboardingDataRefreshInProgress, isUserAuthenticated, adminUsers, userFunctionAccess, showAlert, referenceLookups, refreshData } = useContext(AppContext);

  const [openEditDialogOpen, setOpenEditDialogOpen] = useState<boolean>(false);
  const [selectedUserKey, setSelectedUserKey] = useState<string>("");

  const columns = [
    { field: "userId", headerName: "Login Id", width: 300 },
    { field: "username", headerName: "Name", width: 300 },
    {
      field: "isInternal",
      headerName: "Internal",
      width: 200,
      type: "boolean",
    },
    {
      field: "lastLoginDate",
      headerName: "Last Login Date",
      width: 200,
      type: "date",
      valueFormatter: (params: GridValueFormatterParams) => formatDateForDataGrid(params.value as string),
    },
    {
      field: "roleString",
      headerName: "Assigned Roles",
      width: 3000,
      type: "string",
      hide: true,
    },
  ];

  const formatUserName = (firstName: string, lastName: string) => {
    return firstName + " " + lastName;
  };

  const formatRoles = (adminUserRoles: AdminUserRole[]) => {
    return adminUserRoles.map((adminUserRole) => adminUserRole.roleName).join(" | ");
  };

  const handleDataGridClick = (params: GridRowParams, event: any) => {
    setSelectedUserKey(params.row.userKey);
    setOpenEditDialogOpen(true);
  };

  const newUser = () => {
    setSelectedUserKey(referenceLookups.newRecordDefaultId);
    setOpenEditDialogOpen(true);
  };

  const refresh = () => {
    refreshData("adminuser");
  };

  const getData = () => {
    const rows: any = [];
    adminUsers.map((adminUser, index) =>
      rows.push({
        id: index,
        username: formatUserName(adminUser.firstName, adminUser.lastName),
        roleString: formatRoles(adminUser.roles),
        ...adminUser,
      })
    );
    return rows;
  };

  const saveUserAdmin = (confirmOutput: ConfirmOutput) => {
    setOpenEditDialogOpen(false);
    if (confirmOutput.status === "refresh") {
      refresh();
    }
  };

  useEffect(() => {
    setWindowTitle("Admin Users");
    window.scrollTo(0, 0);
  }, []);



  useEffect(() => {
    const initializePage = async () => {
      checkUserAuthenticated(isUserAuthenticated);

      if (!userFunctionAccess.userPermissionAdmin) {
        showAlert({ message: "Access Denied.", severity: "error" });
        goToRoutePage("home");
        return;
      }
    };
    initializePage();
  }, [isUserAuthenticated, userFunctionAccess.userPermissionAdmin, showAlert]);

  return (
    <React.Fragment>
      {onboardingDataRefreshInProgress ? (
        <SplashWait />
      ) : (
        <Screen>
          <AdminUserCURD open={openEditDialogOpen} userKey={selectedUserKey} onCallback={saveUserAdmin} />

          <Box className={pageStyles.root}>
            <Grid container spacing={3}>
              <Grid item xs={9}>
                <Box className={globalStyles.bottomSpacer}>
                  <Breadcrumbs aria-label="breadcrumb">
                    <Link color="inherit" onClick={() => goToRoutePage("home")} className={globalStyles.link}>
                      Home
                    </Link>
                    <Link color="inherit" onClick={() => goToRoutePage("managesettings")} className={globalStyles.link}>
                      Manage Settings
                    </Link>
                    <Typography color="textPrimary">Users</Typography>
                  </Breadcrumbs>
                </Box>
              </Grid>
              <Grid item xs={3}>
                <Box display="flex" flexDirection="row-reverse">
                  {userFunctionAccess.userPermissionAdmin ? (
                    <>
                      <IconButton onClick={() => newUser()}>
                        <Tooltip arrow title={"Add new user"}>
                          <AddIcon color="secondary" />
                        </Tooltip>
                      </IconButton>
                    </>
                  ) : null}
                  <IconButton onClick={() => refresh()}>
                    <Tooltip arrow title={"Refresh"}>
                      <RefreshIcon color="secondary" />
                    </Tooltip>
                  </IconButton>
                </Box>
              </Grid>
            </Grid>

            <Box className={pageStyles.datagridContainer}>
              <CustomDataGrid uniqueGridCode={"AdminUserListResult"} columns={columns} rows={getData()} showToolBar={true} sortModel={[{ field: "lastLoginDate", sort: "desc" }]} handleDataGridClick={handleDataGridClick} />
            </Box>
          </Box>
        </Screen>
      )}
    </React.Fragment>
  );
};

const LocalPageStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      padding: 0,
    },
    datagridContainer: {
      height: "70vh",
      width: "100%",
      marginBottom: theme.spacing(2),
    },
  })
);

export default ManageAdminUser;
